/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { AnomalyDetails } from "../models/http/anomaly/anomaly-details.model";
import { SingleRowAnomalousApplication } from "../models/http/anomaly/single-row-anomalous-application.model";
import { PagedResponse } from "../models/ui/paged-response.model";
import { AnomalyDetectionHttpService } from "./http/anomaly-detection-http.service";
import { SortDirection } from "ag-grid-community";
import { TableResponse } from "../models/ui/table-response.model";

@Injectable({
	providedIn: "root"
})
export class AnomalyDetectionService {
	constructor(private _anomalyDetectionHttpService: AnomalyDetectionHttpService) {}

	/**
	 * Retrieves a paginated list of anomalous applications for a given tenant.
	 *
	 * @param tenantId - The ID of the tenant.
	 * @param page - The page number to retrieve.
	 * @param size - The number of items per page.
	 * @param sort - (Optional) The field to sort by.
	 * @param applications - (Optional) A list of application IDs to filter by.
	 * @param direction - (Optional) The direction of sorting, either "asc" or "desc".
	 * @returns An Observable that emits a PagedResponse containing TableAnomalyDetection items.
	 */
	public getAllAnomalousApplication(
		tenantId: string,
		page: number,
		size: number,
		startDate: string,
		endDate?: string,
		sort?: string,
		applications?: string[],
		direction?: SortDirection
	): Observable<TableResponse<PagedResponse<SingleRowAnomalousApplication>>> {
		return this._anomalyDetectionHttpService.getAllAnomalousApplication(
			tenantId,
			page,
			size,
			startDate,
			endDate,
			sort,
			applications,
			direction
		);
	}

	/**
	 * Retrieves anomalies for a specific application within a given date range.
	 *
	 * @param applicationId - The ID of the application to retrieve anomalies for.
	 * @param startDate - The start date for the anomaly search range (ISO 8601 format).
	 * @param tenantId - The ID of the tenant.
	 * @param page - The page number for pagination.
	 * @param size - The number of items per page for pagination.
	 * @param endDate - (Optional) The end date for the anomaly search range (ISO 8601 format).
	 * @param layerId - (Optional) An array of layer IDs to filter the anomalies.
	 * @param anomalyType - (Optional) An array of anomaly types to filter the results.
	 * @param sort - (Optional) The field to sort the results by.
	 * @param applications - (Optional) An array of application IDs to filter the anomalies.
	 * @param direction - (Optional) The direction of sorting, either "asc" for ascending or "desc" for descending.
	 * @returns An Observable emitting the details of the anomalies.
	 */
	public getAnomaliesByApplication(
		applicationId: string,
		startDate: string,
		tenantId: string,
		page: number,
		size: number,
		endDate?: string,
		layerId?: string[],
		anomalyType?: string[],
		sort?: string,
		applications?: string[],
		direction?: SortDirection
	): Observable<AnomalyDetails> {
		return this._anomalyDetectionHttpService.getAnomaliesByApplication(
			applicationId,
			startDate,
			tenantId,
			page,
			size,
			endDate,
			layerId,
			anomalyType,
			sort,
			applications,
			direction
		);
	}

	public addUserDeviceByTenant(tenantId: string, payload: string): Observable<string> {
		return this._anomalyDetectionHttpService.addUserDeviceByTenant(tenantId, payload);
	}

	public updateUserNotificationSettings(
		tenantId: string,
		activeDark: boolean,
		payload: string,
		activeMail?: boolean
	): Observable<string> {
		return this._anomalyDetectionHttpService.updateUserNotificationSettings(
			tenantId,
			activeDark,
			payload,
			activeMail
		);
	}
}
